<template>
  <nav class="pagination-container" role="navigation" :aria-label="ariaLabel">
    <ul class="pages-list" role="list">
      <!-- Botão anterior ao lado dos números -->
      <li class="page-item nav-item">
        <button
          class="page-btn nav-btn"
          :disabled="currentPage <= 1"
          @click="goTo(currentPage - 1)"
          @keyup.enter="currentPage > 1 && goTo(currentPage - 1)"
          aria-label="Página anterior"
        >
          ‹
        </button>
      </li>

      <!-- Números da paginação -->
      <li v-for="(item, idx) in pagesToShow" :key="keyFor(item, idx)" class="page-item">
        <button
          v-if="typeof item === 'number'"
          class="page-btn"
          :class="{ active: item === currentPage }"
          @click="goTo(item)"
          @keyup.enter="goTo(item)"
          :aria-label="'Página ' + item"
          :aria-current="item === currentPage ? 'page' : null"
        >
          {{ item }}
        </button>
        <span v-else class="ellipsis" aria-hidden="true">…</span>
      </li>

      <!-- Botão seguinte ao lado dos números -->
      <li class="page-item nav-item">
        <button
          class="page-btn nav-btn"
          :disabled="currentPage >= totalPages"
          @click="goTo(currentPage + 1)"
          @keyup.enter="currentPage < totalPages && goTo(currentPage + 1)"
          aria-label="Página seguinte"
        >
          ›
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'PaginationNumeric',
  props: {
    totalItems: {
      type: Number,
      required: true,
    },
    pageSize: {
      type: Number,
      default: 6,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    maxButtons: {
      type: Number,
      default: 7,
    },
    ariaLabel: {
      type: String,
      default: 'Paginação de lotes',
    },
  },
  computed: {
    totalPages() {
      const size = this.pageSize || 1;
      const total = Math.ceil((this.totalItems || 0) / size);
      return Math.max(1, total);
    },
    pagesToShow() {
      const total = this.totalPages;
      const max = Math.max(5, this.maxButtons); // sempre mostrar pelo menos 5
      const pages = [];

      if (total <= max) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      // Sempre mostrar a primeira e última página
      pages.push(1);

      // Janela central em torno da página atual
      const windowSize = max - 2; // remove first/last
      let start = Math.max(2, this.currentPage - Math.floor(windowSize / 2));
      let end = Math.min(total - 1, start + windowSize - 1);
      // Ajuste quando estamos perto do fim/início
      if (end - start + 1 < windowSize) {
        start = Math.max(2, end - windowSize + 1);
      }

      // Ellipsis à esquerda
      if (start > 2) pages.push('ellipsis-left');

      for (let i = start; i <= end; i++) pages.push(i);

      // Ellipsis à direita
      if (end < total - 1) pages.push('ellipsis-right');

      pages.push(total);
      return pages;
    },
  },
  methods: {
    goTo(page) {
      const target = Math.min(Math.max(1, page), this.totalPages);
      if (target !== this.currentPage) {
        this.$emit('change', target);
      }
    },
    keyFor(item, idx) {
      return typeof item === 'number' ? `page-${item}` : `${item}-${idx}`;
    },
  },
};
</script>

<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0;
}

.pages-list {
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  padding: 0;
  margin: 0;
}

.page-item {
  margin: 0 2px;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 19px;
  border: 1px solid #dcdcdc;
  background: #fff;
  color: #171d1c;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: auto; /* garante que não ocupe 100% */
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.active {
  background: #0783ae; /* cor usada em badges/elementos existentes */
  color: #fff;
  border-color: #0783ae;
}

.nav-btn {
  min-width: 36px;
}

/* garante espaçamento visual consistente entre navegação e números */
.nav-item {
  margin: 0 2px;
}

.ellipsis {
  display: inline-block;
  min-width: 16px;
  text-align: center;
  color: #999;
  padding: 0 6px;
}

@media (max-width: 576px) {
  .page-btn {
    min-width: 32px;
    height: 32px;
    font-size: 13px;
  }
}
</style>